/* eslint-disable import/no-webpack-loader-syntax */
/* eslint-disable import/no-unresolved */
import React from 'react';

// lint-disable-next-line
import { COUNTDOWN_STYLES } from 'es6!sumo/util';
import {
  MAX_UNIT_CIRCLE_VALUES,
  zeroPrefixHelper,
  getLabelHelper,
  handleUnitValue,
  toRgba,
} from './utils';

const unitCircleWrapperStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '64px',
};

const circleWrapperStyle = {
  position: 'relative',
  width: '64px',
  height: '64px',
};

/* CircleCountdown component
 * @param {unit} unit - The countdown unit object containing name and value
 * @param {style} style - The style object to apply to the countdown unit
 * @returns {JSX.Element} - A JSX element representing the countdown unit in a circular format
 */
const CircleCountdownUnit = ({ unit, style }) => {
  const max = MAX_UNIT_CIRCLE_VALUES[unit.name];
  const radius = 32; // Radius of the circle
  const strokeW = 4; // Stroke width of the circle
  const circ = 2 * Math.PI * (radius - strokeW / 2);
  const offset = circ * (1 - unit.value / max);

  const val = handleUnitValue(unit);

  const numberStyle = {
    ...style,
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%,-50%)',
  };

  const circleLabelStyle = {
    marginTop: '6px',
    fontSize: '12px',
    textTransform: 'uppercase',
    color: style.color,
  };

  return (
    <React.Fragment key={unit.name}>
      <div style={unitCircleWrapperStyle}>
        {/* circle + number */}
        <div style={circleWrapperStyle}>
          <svg width="64px" height="64px" style={{ transform: 'rotate(-90deg)' }}>
            {/* border track */}
            <circle
              cx={radius}
              cy={radius}
              r={radius - strokeW / 2}
              fill="none"
              stroke={toRgba(style.color, 0.2)}
              strokeWidth={strokeW}
            />
            {/* progress track */}
            <circle
              cx={radius}
              cy={radius}
              r={radius - strokeW / 2}
              fill={toRgba(style.color, 0.1)}
              stroke={style.color}
              strokeWidth={strokeW}
              strokeLinecap="round"
              strokeDasharray={circ}
              strokeDashoffset={offset}
            />
          </svg>
          {/* number in the middle */}
          <div style={numberStyle}>{val}</div>
        </div>

        {/* label below */}
        <div style={circleLabelStyle}>{getLabelHelper(unit.name, unit.value)}</div>
      </div>
    </React.Fragment>
  );
};

const CustomCountdown = ({
  ref,
  style,
  styleName,
  timeRemaining,
  onMouseEnter,
  onMouseLeave,
}) => {
  const {
    years,
    months,
    days,
    hours,
    minutes,
    seconds,
    value,
  } = timeRemaining;

  const isImpactStyle = styleName === COUNTDOWN_STYLES.impact.value;
  const isCircleStyle = styleName === COUNTDOWN_STYLES.circle.value;
  const isBoxyStyle = styleName === COUNTDOWN_STYLES.boxy.value;
  const isModernStyle = styleName === COUNTDOWN_STYLES.modern.value;
  // Prepare the units to display
  const unitsToDisplay = [];

  // Validate if timeRemaining values exist and are greater than to zero
  if (years > 0) unitsToDisplay.push({ name: 'years', value: years });
  if (months > 0) unitsToDisplay.push({ name: 'months', value: months });
  if (days > 0) unitsToDisplay.push({ name: 'days', value: days });
  // Always display hours, minutes, and seconds
  unitsToDisplay.push({ name: 'hours', value: value > 0 ? hours : 0 });
  unitsToDisplay.push({ name: 'minutes', value: value > 0 ? minutes : 0 });
  unitsToDisplay.push({ name: 'seconds', value: value > 0 ? seconds : 0 });

  const { backgroundColor, color, ...restStyle } = style;

  const unitContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '64px',
    height: '64px',
    ...restStyle,
  };

  const wrapContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '19px',
    justifyContent: 'center',
    height: isModernStyle ? '75px' : '95px',
    backgroundColor,
  };

  const labelStyle = {
    fontSize: '12px',
    fontweight: 700,
    ...(isImpactStyle ? { marginTop: '-10px ' } : {}),
  };

  // If the style is Circle, we want to display the units in a different way
  if (isCircleStyle) {
    restStyle.color = color;

    return (
      <div ref={ref} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
        <div style={wrapContainerStyle}>
          {unitsToDisplay.map((unit) => (
            <CircleCountdownUnit key={unit.name} unit={unit} style={restStyle} />
          ))}
        </div>
      </div>
    );
  }

  if (isBoxyStyle) {
    // For boxy style, we want to apply the color as the background color
    // and the backgroundColor as the text color
    unitContainerStyle.backgroundColor = color;
    unitContainerStyle.color = backgroundColor;
  } else {
    // For other styles, we apply the color as usual
    restStyle.color = color;
    unitContainerStyle.color = color;
  }

  return (
    <div ref={ref} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <div style={wrapContainerStyle}>
        {unitsToDisplay.map((unit, i) => {
          const isLast = i === unitsToDisplay.length - 1;
          const val = isImpactStyle ? zeroPrefixHelper(unit.value) : handleUnitValue(unit);

          return (
            <React.Fragment key={unit.name}>
              <div style={unitContainerStyle}>
                <div>{val}</div>
                <div style={labelStyle}>{getLabelHelper(unit.name, unit.value)}</div>
              </div>
              {/* colon *between* columns */}
              {isImpactStyle && !isLast && (
                <div style={{ ...restStyle, marginTop: '-14px' }}>:</div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default CustomCountdown;
